/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import MsDyn365 from '@msdyn365-commerce/core';
import { HtmlHeadInclude } from '@msdyn365-commerce/core-internal';
import classnames from 'classnames';
import * as React from 'react';
import { IBreadcrumbViewProps } from './mfrm-breadcrumb';
import CustomSkeleton from '../../themes/mattressfirm/custom-components/CustomSkeleton';

// tslint:disable-next-line: cyclomatic-complexity
const MfrmBreadCrumbView: React.FC<IBreadcrumbViewProps> = props => {
    const { root, isLoadedRoot, links, backButton, BreadcrumbContainer, BreadcrumbList, BreadcrumbListItem, separator, plpBreadcrumbSchema, pdpBreadcrumbSchema } = props;
    // VSI Customization - start
    // Todo
    // To remove channel navigation hirarchy parent node i.e. All, which might be updated later on
    const levelToHide = 1;
    let showBreadCrumb: boolean = false;
    const pageType = props.data.listPageState?.result?.pageType;
    const searchQueryValue = props.context.request.query?.q;
    const mfrmLinks = links?.length && props.config.hideParentCatagory ? links?.splice(levelToHide, links.length) : links;
    const pageURL = MsDyn365.isBrowser && window.location.pathname;
    const activeProducts = props.data.listPageState?.result?.activeProducts;
    const extProperties = activeProducts && activeProducts.length > 0 && activeProducts[0]?.ExtensionProperties || [];
    const fallBackQueryResult = extProperties?.find(obj => obj?.Key === 'FallbackQuery')?.Value?.StringValue;
    const [isSchemaUpdated,setIsSchemaUpdated]=React.useState(false);
    const getHREF = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        event.preventDefault();
        const targetElm = event.target as Element;
        const parentHREF = targetElm?.closest('a')?.getAttribute('href'); // get clicked items parent href
        const redirectURL = parentHREF; // set redirect url after tagging even
        // @ts-ignore
        if (utag && redirectURL !== undefined) {
            event.preventDefault();
            // @ts-ignore
            // tslint:disable-next-line: object-literal-key-quotes
            utag.link({
                event: 'internal navigation',
                event_category: 'internal links',
                event_action: `to: ${redirectURL}`,
                event_label: `from: ${pageURL}`,
                event_noninteraction: 'false'
            });
            if (MsDyn365.isBrowser && redirectURL) {
                window.location.href = redirectURL;
            }
        }
    };
    if (pageType === 'Search' && props.data.updateBreadcrumbOnSrp.result?.breadcrumbCheck) {
        showBreadCrumb = true;
    } else if (pageType === 'Search' && !props.data.updateBreadcrumbOnSrp.result?.breadcrumbCheck) {
        showBreadCrumb = false;
    }
    // VSI Customization - ends
    if (!root && !links && !backButton) {
        props.context.telemetry.warning('BreadCrumb content is empty, module wont render');
        return null;
    }
    const _renderPlpBreadcrumbSchema =()=> {
        if (plpBreadcrumbSchema) {
            const inlineScripts = document.createElement('script');
            inlineScripts .setAttribute('type', 'application/ld+json');
            inlineScripts.text = plpBreadcrumbSchema;
            const head = document.head;
            head.insertBefore(inlineScripts, head.firstElementChild);
        }
    };

    React.useEffect(() => {
      if(plpBreadcrumbSchema){
        setIsSchemaUpdated(true);
      }
    } ,[plpBreadcrumbSchema]);
    if(!isLoadedRoot){
        return <CustomSkeleton className='ms-breadcrumb_skeleton-dimensions' height='12px' type='text' style={{marginBottom:'0'}}/>;
    } else {
        return (
            <Module {...BreadcrumbContainer}>
                {isSchemaUpdated && (pageType ==='Category' || pageType ==='Search') && _renderPlpBreadcrumbSchema() }
                {props.context.request.urlTokens.pageType === 'Product' &&
                <HtmlHeadInclude>
                {/* tslint:disable:react-no-dangerous-html - intentionally setting inner html with only data action output (no unsanizted user input)*/}
                <script data-load-point='headEnd' type='application/ld+json' dangerouslySetInnerHTML={{ __html: pdpBreadcrumbSchema }} />
                </HtmlHeadInclude> }
                <Node {...BreadcrumbList}>
                    {root && pageType !== 'Search' && (
                        <Node {...BreadcrumbListItem}>
                            {root}
                            {links && separator}
                        </Node>
                    )}
                    {root && pageType === 'Search' && !showBreadCrumb && (
                        <Node {...BreadcrumbListItem}>
                            {root}
                            {separator}
                            {props.resources.breadcrumbShowingResultQuery} {fallBackQueryResult !== '' ? <span className='breadcrumb_item-query'>‘‘{fallBackQueryResult}’’</span> : <span className='breadcrumb_item-query'>‘‘{searchQueryValue}’’</span>}
                        </Node>
                    )}
                    {backButton && <Node {...BreadcrumbListItem}>{backButton}</Node>}
                    {/* VSI Customization -start */}
                    {mfrmLinks &&
                        mfrmLinks.map((link, index) => (
                            <React.Fragment key={index}>
                                <Node
                                    {...BreadcrumbListItem}
                                    className={classnames(
                                        BreadcrumbListItem.className,
                                        index === mfrmLinks.length - 1 ? 'breadcrumb_item-current' : ''
                                    )}
                                    onClick={getHREF}
                                >
                                    {link}
                                    {mfrmLinks[index + 1] && separator}
                                </Node>
                            </React.Fragment>
                        ))}
                    {/* VSI Customization - ends */}
                </Node>
            </Module>
        );
    }
};

export default MfrmBreadCrumbView;
